/* Flyer template library — fixed 1080x1350 (Instagram 4:5 portrait) designs.
   Each template receives { brand, copy, backgroundUrl } and renders crisp text
   with the agent's REAL logo/headshot. Exported to PNG by html-to-image. */
import React from 'react';

const W = 1080;
const H = 1350;

const heading = { fontFamily: "var(--flyer-heading, 'Montserrat', sans-serif)" };
const bodyFont = { fontFamily: "var(--flyer-body, 'Poppins', sans-serif)" };

export const FONT_PAIRS = [
  { id: 'montserrat-poppins', name: 'Montserrat · Poppins (modern)', heading: "'Montserrat', sans-serif", body: "'Poppins', sans-serif" },
  { id: 'anton-poppins', name: 'Anton · Poppins (bold impact)', heading: "'Anton', sans-serif", body: "'Poppins', sans-serif" },
  { id: 'bebas-poppins', name: 'Bebas Neue · Poppins (tall caps)', heading: "'Bebas Neue', sans-serif", body: "'Poppins', sans-serif" },
  { id: 'oswald-roboto', name: 'Oswald · Roboto (condensed)', heading: "'Oswald', sans-serif", body: "'Roboto', sans-serif" },
  { id: 'playfair-poppins', name: 'Playfair · Poppins (luxury serif)', heading: "'Playfair Display', serif", body: "'Poppins', sans-serif" },
  { id: 'archivo-lora', name: 'Archivo Black · Lora (editorial)', heading: "'Archivo Black', sans-serif", body: "'Lora', serif" },
];

function Img({ src, style, alt = '' }) {
  if (!src) return null;
  return <img src={src} alt={alt} crossOrigin="anonymous" style={{ display: 'block', ...style }} />;
}

function ContactBar({ brand, dark }) {
  const fg = dark ? '#FFFFFF' : brand.colors?.text || '#0F1B2D';
  const sub = dark ? 'rgba(255,255,255,0.85)' : '#475569';
  const lic = brand.license_no ? `${brand.license_state || ''} Lic #${brand.license_no}` : '';
  return (
    <div style={{ ...bodyFont, color: fg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: 30, fontWeight: 700 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 40, height: 40, borderRadius: 999, background: brand.colors?.secondary || '#E27A22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22 }}>✆</span>
          {brand.phone}
        </span>
        {brand.website && <span style={{ color: sub, fontWeight: 600 }}>· {brand.website}</span>}
      </div>
      {lic && <div style={{ fontSize: 20, color: sub, marginTop: 6, fontWeight: 500 }}>{lic}{brand.states?.length ? ` · Serving ${brand.states.join(', ')}` : ''}</div>}
    </div>
  );
}

function Headshot({ brand, size = 240, ring }) {
  if (!brand.headshot_url) return null;
  const dim = `calc(${size}px * var(--flyer-headshot-scale, 1))`;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 999, overflow: 'hidden', border: `8px solid ${ring || brand.colors?.secondary || '#E27A22'}`, boxShadow: '0 18px 40px rgba(0,0,0,0.35)', background: '#fff' }}>
      <Img src={brand.headshot_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
}

function Disclaimer({ brand, dark }) {
  if (!brand.disclaimer) return null;
  return <div style={{ ...bodyFont, fontSize: 16, color: dark ? 'rgba(255,255,255,0.6)' : '#94A3B8', marginTop: 14, lineHeight: 1.3, textAlign: 'var(--flyer-disclaimer-align, left)' }}>{brand.disclaimer}</div>;
}

function Chips({ items, color, textColor = '#fff' }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {(items || []).slice(0, 6).map((c, i) => (
        <span key={i} style={{ ...bodyFont, fontSize: 24, fontWeight: 600, color: textColor, background: color, padding: '10px 20px', borderRadius: 999 }}>{c}</span>
      ))}
    </div>
  );
}

/* ---- Template 1: Bold Hero (full photo + bottom gradient) ---- */
function BoldHero({ brand, copy, backgroundUrl }) {
  const c = brand.colors || {};
  const cov = copy.coverage_highlights?.length ? copy.coverage_highlights : brand.coverage_options;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: c.primary_dark || '#0A3D7A', overflow: 'hidden' }}>
      <Img src={backgroundUrl} style={{ position: 'absolute', inset: 0, width: W, height: H, objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(8,18,40,0.15) 0%, rgba(8,18,40,0.2) 38%, ${hexA(c.primary_dark || '#0A3D7A', 0.86)} 72%, ${hexA(c.primary_dark || '#0A1224', 0.97)} 100%)` }} />
      {/* top bar */}
      <div style={{ position: 'absolute', top: 44, left: 56, right: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Img src={brand.logo_url} style={{ height: 'calc(96px * var(--flyer-logo-scale, 1))', width: 'auto', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.4))' }} />
        <span style={{ ...heading, color: '#fff', fontWeight: 800, fontSize: 26, background: hexA(c.secondary || '#E27A22', 0.95), padding: '10px 22px', borderRadius: 999 }}>{copy.theme_name || ''}</span>
      </div>
      {/* bottom content */}
      <div style={{ position: 'absolute', left: 56, right: 56, bottom: 52 }}>
        <Headshot brand={brand} size={200} />
        <div style={{ ...heading, color: '#fff', fontWeight: 900, fontSize: 86, lineHeight: 1.02, marginTop: 22, textShadow: '0 4px 20px rgba(0,0,0,0.45)' }}>{copy.headline}</div>
        {copy.subhead && <div style={{ ...bodyFont, color: c.accent || '#F2954A', fontWeight: 700, fontSize: 34, marginTop: 12 }}>{copy.subhead}</div>}
        {copy.body && <div style={{ ...bodyFont, color: 'rgba(255,255,255,0.92)', fontSize: 27, lineHeight: 1.4, marginTop: 14, maxWidth: 860 }}>{copy.body}</div>}
        <div style={{ marginTop: 22 }}><Chips items={cov} color={hexA('#FFFFFF', 0.16)} /></div>
        <div style={{ ...heading, display: 'inline-block', marginTop: 26, background: c.secondary || '#E27A22', color: '#fff', fontWeight: 800, fontSize: 34, padding: '18px 34px', borderRadius: 16, boxShadow: '0 14px 30px rgba(0,0,0,0.4)' }}>{copy.cta}</div>
        <div style={{ marginTop: 26 }}><ContactBar brand={brand} dark /></div>
        <Disclaimer brand={brand} dark />
      </div>
    </div>
  );
}

/* ---- Template 2: Split Panel ---- */
function SplitPanel({ brand, copy, backgroundUrl }) {
  const c = brand.colors || {};
  const cov = copy.coverage_highlights?.length ? copy.coverage_highlights : brand.coverage_options;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: '#fff', overflow: 'hidden', display: 'flex' }}>
      <div style={{ width: 560, height: H, background: c.primary || '#1E5BA8', padding: 56, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <Img src={brand.logo_url} style={{ height: 'calc(92px * var(--flyer-logo-scale, 1))', width: 'auto', background: '#fff', padding: 10, borderRadius: 14 }} />
          <div style={{ ...heading, color: '#fff', fontWeight: 900, fontSize: 'calc(76px * var(--flyer-hscale, 1))', lineHeight: 1.03, marginTop: 40 }}>{copy.headline}</div>
          {copy.subhead && <div style={{ ...bodyFont, color: c.accent || '#F2954A', fontWeight: 700, fontSize: 30, marginTop: 14 }}>{copy.subhead}</div>}
          {copy.body && <div style={{ ...bodyFont, color: 'rgba(255,255,255,0.9)', fontSize: 24, lineHeight: 1.4, marginTop: 16 }}>{copy.body}</div>}
        </div>
        <div>
          <div style={{ marginBottom: 18 }}><Chips items={cov} color={hexA('#FFFFFF', 0.18)} /></div>
          <div style={{ ...heading, display: 'inline-block', background: c.secondary || '#E27A22', color: '#fff', fontWeight: 800, fontSize: 30, padding: '16px 28px', borderRadius: 14 }}>{copy.cta}</div>
        </div>
      </div>
      <div style={{ position: 'relative', flex: 1, height: H }}>
        <Img src={backgroundUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: H, objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0) 50%, ${hexA(c.primary_dark || '#0A3D7A', 0.92)} 100%)` }} />
        <div style={{ position: 'absolute', right: 40, top: 40 }}><Headshot brand={brand} size={210} ring="#fff" /></div>
        <div style={{ position: 'absolute', left: 40, right: 40, bottom: 44 }}>
          <ContactBar brand={brand} dark />
          <Disclaimer brand={brand} dark />
        </div>
      </div>
    </div>
  );
}

/* ---- Template 3: Luxury (dark navy + gold) ---- */
function Luxury({ brand, copy, backgroundUrl }) {
  const c = brand.colors || {};
  const cov = copy.coverage_highlights?.length ? copy.coverage_highlights : brand.coverage_options;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: c.primary_dark || '#0A1F3D', overflow: 'hidden', padding: 64, boxSizing: 'border-box' }}>
      <Img src={backgroundUrl} style={{ position: 'absolute', inset: 0, width: W, height: H, objectFit: 'cover', opacity: 0.28 }} />
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 50% 30%, ${hexA(c.primary || '#1E5BA8', 0.35)}, ${hexA(c.primary_dark || '#0A1224', 0.98)})` }} />
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'space-between' }}>
        <Img src={brand.logo_url} style={{ height: 'calc(110px * var(--flyer-logo-scale, 1))', width: 'auto' }} />
        <div>
          <div style={{ ...bodyFont, color: c.secondary || '#E2B04A', letterSpacing: 6, fontSize: 26, fontWeight: 700, textTransform: 'uppercase' }}>{copy.theme_name}</div>
          <div style={{ ...heading, color: '#fff', fontWeight: 900, fontSize: 'calc(90px * var(--flyer-hscale, 1))', lineHeight: 1.02, marginTop: 18 }}>{copy.headline}</div>
          <div style={{ width: 120, height: 5, background: c.secondary || '#E2B04A', margin: '26px auto', borderRadius: 9 }} />
          {copy.subhead && <div style={{ ...bodyFont, color: 'rgba(255,255,255,0.92)', fontSize: 32, fontWeight: 600 }}>{copy.subhead}</div>}
          {copy.body && <div style={{ ...bodyFont, color: 'rgba(255,255,255,0.78)', fontSize: 25, lineHeight: 1.45, marginTop: 18, maxWidth: 820 }}>{copy.body}</div>}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 26 }}><Chips items={cov} color={hexA(c.secondary || '#E2B04A', 0.18)} textColor={c.secondary || '#E2B04A'} /></div>
        </div>
        <div style={{ width: '100%' }}>
          <div style={{ ...heading, display: 'inline-block', background: c.secondary || '#E2B04A', color: c.primary_dark || '#0A1F3D', fontWeight: 800, fontSize: 34, padding: '18px 40px', borderRadius: 999, marginBottom: 26 }}>{copy.cta}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ContactBar brand={brand} dark /></div>
          <Disclaimer brand={brand} dark />
        </div>
      </div>
    </div>
  );
}

/* ---- Template 4: Coverage Checklist ---- */
function CoverageList({ brand, copy, backgroundUrl }) {
  const c = brand.colors || {};
  const cov = copy.coverage_highlights?.length ? copy.coverage_highlights : brand.coverage_options;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: c.light || '#F7F6F1', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 360, background: c.primary || '#1E5BA8' }} />
      <Img src={backgroundUrl} style={{ position: 'absolute', top: 0, left: 0, right: 0, width: W, height: 360, objectFit: 'cover', opacity: 0.5 }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 360, background: `linear-gradient(180deg, ${hexA(c.primary || '#1E5BA8', 0.55)}, ${hexA(c.primary_dark || '#0A3D7A', 0.92)})` }} />
      <div style={{ position: 'absolute', top: 48, left: 56, right: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Img src={brand.logo_url} style={{ height: 'calc(90px * var(--flyer-logo-scale, 1))', background: '#fff', padding: 10, borderRadius: 12 }} />
        <Headshot brand={brand} size={180} ring="#fff" />
      </div>
      <div style={{ position: 'absolute', top: 230, left: 56, right: 56 }}>
        <div style={{ ...heading, color: '#fff', fontWeight: 900, fontSize: 'calc(70px * var(--flyer-hscale, 1))', lineHeight: 1.04, textShadow: '0 3px 14px rgba(0,0,0,0.4)' }}>{copy.headline}</div>
      </div>
      <div style={{ position: 'absolute', top: 420, left: 56, right: 56 }}>
        {copy.body && <div style={{ ...bodyFont, color: c.text || '#0F1B2D', fontSize: 27, lineHeight: 1.4, marginBottom: 24 }}>{copy.body}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px 28px' }}>
          {(cov || []).slice(0, 8).map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ width: 44, height: 44, borderRadius: 999, background: c.secondary || '#E27A22', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 800, flex: '0 0 auto' }}>✓</span>
              <span style={{ ...bodyFont, fontSize: 27, fontWeight: 600, color: c.text || '#0F1B2D' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: 'absolute', left: 56, right: 56, bottom: 50 }}>
        <div style={{ ...heading, textAlign: 'center', background: c.secondary || '#E27A22', color: '#fff', fontWeight: 800, fontSize: 34, padding: '20px', borderRadius: 16, marginBottom: 22 }}>{copy.cta}</div>
        <ContactBar brand={brand} />
        <Disclaimer brand={brand} />
      </div>
    </div>
  );
}

/* ---- Template 5: Minimal Clean ---- */
function MinimalClean({ brand, copy, backgroundUrl }) {
  const c = brand.colors || {};
  const cov = copy.coverage_highlights?.length ? copy.coverage_highlights : brand.coverage_options;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: '#fff', overflow: 'hidden', padding: 64, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 360, height: 360, background: hexA(c.secondary || '#E27A22', 0.12), borderBottomLeftRadius: 360 }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: 280, height: 280, background: hexA(c.primary || '#1E5BA8', 0.1), borderTopRightRadius: 280 }} />
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Img src={brand.logo_url} style={{ height: 'calc(100px * var(--flyer-logo-scale, 1))', width: 'auto' }} />
        <Headshot brand={brand} size={170} />
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{ ...bodyFont, color: c.secondary || '#E27A22', fontWeight: 800, fontSize: 28, letterSpacing: 2, textTransform: 'uppercase' }}>{copy.theme_name}</div>
        <div style={{ ...heading, color: c.text || '#0F1B2D', fontWeight: 900, fontSize: 'calc(96px * var(--flyer-hscale, 1))', lineHeight: 1.0, marginTop: 14 }}>{copy.headline}</div>
        <div style={{ width: 130, height: 8, background: c.secondary || '#E27A22', borderRadius: 9, margin: '28px 0' }} />
        {copy.subhead && <div style={{ ...bodyFont, color: c.primary || '#1E5BA8', fontWeight: 700, fontSize: 36, marginBottom: 14 }}>{copy.subhead}</div>}
        {copy.body && <div style={{ ...bodyFont, color: '#475569', fontSize: 28, lineHeight: 1.45, maxWidth: 860 }}>{copy.body}</div>}
        <div style={{ marginTop: 26 }}><Chips items={cov} color={c.primary || '#1E5BA8'} /></div>
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{ ...heading, display: 'inline-block', background: c.secondary || '#E27A22', color: '#fff', fontWeight: 800, fontSize: 34, padding: '18px 36px', borderRadius: 14, marginBottom: 24 }}>{copy.cta}</div>
        <ContactBar brand={brand} />
        <Disclaimer brand={brand} />
      </div>
    </div>
  );
}

function hexA(hex, a) {
  const h = (hex || '#000000').replace('#', '');
  const n = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export const FLYER_TEMPLATES = [
  { id: 'bold-hero', name: 'Bold Hero', Component: BoldHero },
  { id: 'split-panel', name: 'Split Panel', Component: SplitPanel },
  { id: 'luxury', name: 'Luxury', Component: Luxury },
  { id: 'coverage-list', name: 'We Cover (Checklist)', Component: CoverageList },
  { id: 'minimal', name: 'Minimal Clean', Component: MinimalClean },
];

export const FLYER_W = W;
export const FLYER_H = H;

const QR_POS = {
  'bottom-right': { bottom: 44, right: 44 }, 'bottom-left': { bottom: 44, left: 44 },
  'top-right': { top: 44, right: 44 }, 'top-left': { top: 44, left: 44 },
  'center': { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
};

export function FlyerCanvas({ templateId, brand, copy, backgroundUrl, innerRef, fonts, scale = 1, layout, qr }) {
  const tpl = FLYER_TEMPLATES.find((t) => t.id === templateId) || FLYER_TEMPLATES[0];
  const C = tpl.Component;
  const L = layout || {};
  const styleVars = {
    position: 'relative', width: W, height: H,
    '--flyer-heading': fonts?.heading || "'Montserrat', sans-serif",
    '--flyer-body': fonts?.body || "'Poppins', sans-serif",
    '--flyer-hscale': (L.headlineScale != null ? L.headlineScale : scale) || 1,
    '--flyer-logo-scale': L.logoScale != null ? L.logoScale : 1,
    '--flyer-headshot-scale': L.headshotScale != null ? L.headshotScale : 1,
    '--flyer-disclaimer-align': L.disclaimerAlign || 'left',
  };
  const qrOn = qr?.dataUrl && qr?.enabled !== false;
  const qrSize = qr?.size || 200;
  return (
    <div ref={innerRef} style={styleVars}>
      <C brand={brand} copy={copy} backgroundUrl={backgroundUrl} />
      {qrOn && (
        <div style={{ position: 'absolute', ...(QR_POS[qr.position] || QR_POS['bottom-right']), zIndex: 5,
          background: '#fff', padding: 12, borderRadius: 16, boxShadow: '0 10px 28px rgba(0,0,0,0.3)', textAlign: 'center' }}>
          <img src={qr.dataUrl} alt="QR code" style={{ display: 'block', width: qrSize, height: qrSize }} crossOrigin="anonymous" />
          {qr.label && <div style={{ ...bodyFont, fontSize: Math.max(14, qrSize * 0.1), fontWeight: 800, color: brand?.colors?.primary || '#1E5BA8', marginTop: 6 }}>{qr.label}</div>}
        </div>
      )}
    </div>
  );
}
